import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Filter, X, Clock, TrendingUp, Loader2 } from 'lucide-react';
import searchService, { SearchResult } from '../services/searchService';

interface AdvancedSearchBarProps {
  onSearch?: (results: SearchResult[]) => void;
  onQueryChange?: (query: string) => void;
  placeholder?: string;
  showFilters?: boolean;
  showSuggestions?: boolean;
  autoSearch?: boolean;
  className?: string;
}

export default function AdvancedSearchBar({
  onSearch,
  onQueryChange,
  placeholder = "Search for businesses, services, or keywords...",
  showFilters = true,
  showSuggestions = true,
  autoSearch = false,
  className = ""
}: AdvancedSearchBarProps) {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [businessType, setBusinessType] = useState<'trader' | 'craftie' | 'hospitality' | 'professional' | ''>('');
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestionDropdown, setShowSuggestionDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [popularSearches, setPopularSearches] = useState<Array<{query: string, count: number}>>([]);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchStats, setSearchStats] = useState<{
    totalResults: number;
    hasExactMatches: boolean;
    hasFuzzyMatches: boolean;
    searchTime: number;
  } | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Load popular searches on mount
  useEffect(() => {
    const loadPopularSearches = async () => {
      try {
        const popular = await searchService.getPopularSearches(8);
        setPopularSearches(popular);
      } catch (error) {
        console.error('Failed to load popular searches:', error);
      }
    };

    loadPopularSearches();
  }, []);

  // Auto-search with debounce
  useEffect(() => {
    if (autoSearch && query.trim().length > 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch();
      }, 500);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, location, businessType, autoSearch]);

  // Get suggestions when query changes
  useEffect(() => {
    const getSuggestions = async () => {
      if (query.trim().length > 1 && showSuggestions) {
        try {
          const newSuggestions = await searchService.getSearchSuggestions(query.trim(), 5);
          setSuggestions(newSuggestions);
        } catch (error) {
          console.error('Failed to get suggestions:', error);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    };

    const debounceTimeout = setTimeout(getSuggestions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [query, showSuggestions]);

  // Handle query change
  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    onQueryChange?.(newQuery);
    
    if (newQuery.trim().length > 0) {
      setShowSuggestionDropdown(true);
    }
  };

  // Handle search
  const handleSearch = async (searchQuery?: string) => {
    const finalQuery = searchQuery || query;
    
    if (!finalQuery.trim()) {
      return;
    }

    // Validate query
    const validation = searchService.validateSearchQuery(finalQuery);
    if (!validation.isValid) {
      console.error('Invalid search query:', validation.error);
      return;
    }

    setIsSearching(true);
    setShowSuggestionDropdown(false);

    try {
      const response = await searchService.searchBusinesses({
        query: finalQuery.trim(),
        location: location.trim() || undefined,
        businessType: businessType || undefined,
        limit: 50,
        similarityThreshold: 0.3
      });

      const formattedResults = searchService.formatSearchResults(response.results);
      
      setSearchResults(formattedResults);
      setSearchStats({
        totalResults: response.totalCount,
        hasExactMatches: response.hasExactMatches,
        hasFuzzyMatches: response.hasFuzzyMatches,
        searchTime: response.searchTime
      });

      onSearch?.(formattedResults);

      console.log('🔍 Search completed:', {
        query: finalQuery,
        results: formattedResults.length,
        stats: searchService.getSearchStats(formattedResults)
      });

    } catch (error) {
      console.error('❌ Search failed:', error);
      setSearchResults([]);
      setSearchStats(null);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestionDropdown(false);
    handleSearch(suggestion);
  };

  // Handle popular search click
  const handlePopularSearchClick = (popularQuery: string) => {
    setQuery(popularQuery);
    setShowSuggestionDropdown(false);
    handleSearch(popularQuery);
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  // Handle clear
  const handleClear = () => {
    setQuery('');
    setLocation('');
    setBusinessType('');
    setSearchResults([]);
    setSearchStats(null);
    setShowSuggestionDropdown(false);
    searchInputRef.current?.focus();
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative w-full max-w-4xl mx-auto ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        {/* Main Search Bar */}
        <div className="flex flex-col md:flex-row bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Search Input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onFocus={() => query.length > 0 && setShowSuggestionDropdown(true)}
              className="w-full pl-12 pr-4 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 border-0"
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Location Input */}
          <div className="relative border-t md:border-t-0 md:border-l border-gray-200">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MapPin className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Location (optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full md:w-48 pl-12 pr-4 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 border-0"
            />
          </div>

          {/* Filters Button */}
          {showFilters && (
            <button
              type="button"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={`px-4 py-4 border-t md:border-t-0 md:border-l border-gray-200 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors ${
                showFiltersPanel ? 'bg-gray-50 text-gray-800' : ''
              }`}
            >
              <Filter className="h-5 w-5" />
            </button>
          )}

          {/* Search Button */}
          <button
            type="submit"
            disabled={isSearching || !query.trim()}
            className="px-8 py-4 bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[120px]"
          >
            {isSearching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>

        {/* Filters Panel */}
        {showFiltersPanel && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Type
                </label>
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value as any)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="trader">Traders</option>
                  <option value="craftie">Crafties</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="professional">Professionals</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Suggestions Dropdown */}
        {showSuggestionDropdown && showSuggestions && (suggestions.length > 0 || popularSearches.length > 0) && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-y-auto z-20"
          >
            {/* Recent Suggestions */}
            {suggestions.length > 0 && (
              <div className="p-2">
                <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500">
                  <Clock className="h-4 w-4" />
                  Suggestions
                </div>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            {/* Popular Searches */}
            {popularSearches.length > 0 && (
              <div className="p-2 border-t border-gray-100">
                <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500">
                  <TrendingUp className="h-4 w-4" />
                  Popular Searches
                </div>
                {popularSearches.slice(0, 5).map((popular, index) => (
                  <button
                    key={index}
                    onClick={() => handlePopularSearchClick(popular.query)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex justify-between items-center"
                  >
                    <span>{popular.query}</span>
                    <span className="text-xs text-gray-400">{popular.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </form>

      {/* Search Stats */}
      {searchStats && (
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <span>
            {searchStats.totalResults} result{searchStats.totalResults !== 1 ? 's' : ''}
          </span>
          <span>
            {searchStats.searchTime.toFixed(0)}ms
          </span>
          {searchStats.hasFuzzyMatches && (
            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
              Including similar matches
            </span>
          )}
          {searchStats.hasExactMatches && (
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
              Exact matches found
            </span>
          )}
        </div>
      )}
    </div>
  );
}
